import { Injectable, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from './logger.service';
import { BrowserHandlerService } from './browser-handler.service';

/**
 * Keyboard Detection Service
 * Detects when the virtual keyboard is visible on mobile devices and tablets
 * Uses multiple detection methods for better reliability, especially on tablets
 * 
 * Features:
 * - Visual Viewport API (most reliable)
 * - Input focus/blur events
 * - Device-specific thresholds (tablets vs phones)
 * - Orientation-aware detection
 * - Observable for keyboard state changes
 */
@Injectable({
  providedIn: 'root'
})
export class KeyboardDetectionService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly browserHandler = inject(BrowserHandlerService);

  private readonly keyboardVisibleSubject = new BehaviorSubject<boolean>(false);
  public readonly keyboardVisible$: Observable<boolean> = this.keyboardVisibleSubject.asObservable();

  private initialViewportHeight: number = 0;
  private lastKnownHeight: number = 0;
  private isTablet: boolean = false;
  private isIPad: boolean = false;
  private deviceType: 'phone' | 'tablet' | 'pc' | null = null;
  
  private visualViewportListener?: () => void;
  private resizeListener?: () => void;
  private orientationChangeListener?: () => void;
  private inputFocusListener?: (event: Event) => void;
  private inputBlurListener?: (event: Event) => void;
  
  private focusCheckTimeouts: ReturnType<typeof setTimeout>[] = [];
  private resizeDebounceTimeout?: ReturnType<typeof setTimeout>;
  private initialized = false;

  /**
   * Initialize keyboard detection
   * Should be called once from AppComponent
   */
  init(): void {
    if (this.initialized) return;
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('KeyboardDetectionService.init() called in non-browser environment');
      return;
    }

    try {
      // Get device type from BrowserHandlerService
      this.deviceType = this.browserHandler.deviceDetection();
      this.isTablet = this.deviceType === 'tablet';
      
      // Detect iPad specifically (needs special handling)
      this.isIPad = this.detectIPad();
      
      // Only initialize for mobile devices and tablets
      if (this.deviceType === 'pc') {
        this.logger.log('KeyboardDetectionService: Skipping initialization on desktop');
        return;
      }

      // Store initial viewport height
      this.updateInitialViewportHeight();

      // Setup listeners
      this.setupOrientationListener();
      this.setupViewportListeners();
      this.setupInputListeners();

      this.initialized = true;
      this.logger.log('KeyboardDetectionService initialized', {
        deviceType: this.deviceType,
        isTablet: this.isTablet,
        isIPad: this.isIPad
      });
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'init' });
    }
  }

  /**
   * Check if keyboard is currently visible
   */
  isKeyboardVisible(): boolean {
    return this.keyboardVisibleSubject.value;
  }

  /**
   * Detect iPad specifically
   * iPad detection is tricky because newer iPads report as MacIntel
   */
  private detectIPad(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const ua = navigator.userAgent;
    const platform = navigator.platform;

    // Direct iPad detection
    if (/iPad/i.test(ua)) {
      return true;
    }

    // iPadOS 13+ detection (reports as MacIntel)
    if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      // Additional check: iPad has specific screen characteristics
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      
      // iPad Pro 12.9" (1024x1366), iPad Air/Pro 11" (834x1194), iPad (768x1024)
      const isIPadScreenSize = 
        (screenWidth === 1024 && screenHeight === 1366) ||
        (screenWidth === 1366 && screenHeight === 1024) ||
        (screenWidth === 834 && screenHeight === 1194) ||
        (screenWidth === 1194 && screenHeight === 834) ||
        (screenWidth === 768 && screenHeight === 1024) ||
        (screenWidth === 1024 && screenHeight === 768);

      if (isIPadScreenSize) {
        return true;
      }

      // Fallback: Check if it's a touch device with MacIntel platform
      // This is a common pattern for iPadOS 13+
      return true; // Conservative approach for MacIntel + touch
    }

    return false;
  }

  /**
   * Update initial viewport height
   * Should be called when keyboard is definitely closed
   */
  private updateInitialViewportHeight(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Only update if keyboard is not currently visible
    if (!this.keyboardVisibleSubject.value) {
      const visualViewportHeight = window.visualViewport?.height;
      const windowHeight = window.innerHeight;
      
      // Use visual viewport if available, otherwise window height
      const height = visualViewportHeight || windowHeight;
      
      if (height > 0) {
        this.initialViewportHeight = height;
        this.lastKnownHeight = height;
      }
    }
  }

  /**
   * Setup orientation change listener
   */
  private setupOrientationListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.orientationChangeListener = () => {
        // Delay to allow viewport to settle after orientation change
        setTimeout(() => {
          this.updateInitialViewportHeight();
          // Re-check keyboard state after orientation change
          this.checkKeyboardState();
        }, 300);
      };

      window.addEventListener('orientationchange', this.orientationChangeListener);
      window.addEventListener('resize', this.orientationChangeListener);
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'setupOrientationListener' });
    }
  }

  /**
   * Setup viewport listeners (Visual Viewport API preferred)
   */
  private setupViewportListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Use Visual Viewport API if available (most reliable)
      if (window.visualViewport) {
        this.visualViewportListener = () => {
          this.handleViewportChange();
        };

        window.visualViewport.addEventListener('resize', this.visualViewportListener);
        window.visualViewport.addEventListener('scroll', this.visualViewportListener);
      } else {
        // Fallback to window resize event
        this.resizeListener = () => {
          this.handleViewportChange();
        };
        window.addEventListener('resize', this.resizeListener);
      }
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'setupViewportListeners' });
    }
  }

  /**
   * Setup input focus/blur listeners
   */
  private setupInputListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.inputFocusListener = (event: Event) => {
        const target = event.target as HTMLElement;
        if (this.isInputElement(target)) {
          this.handleInputFocus(target);
        }
      };

      this.inputBlurListener = (event: Event) => {
        const target = event.target as HTMLElement;
        if (this.isInputElement(target)) {
          this.handleInputBlur();
        }
      };

      document.addEventListener('focusin', this.inputFocusListener, true);
      document.addEventListener('focusout', this.inputBlurListener, true);
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'setupInputListeners' });
    }
  }

  /**
   * Check if element is an input element
   */
  private isInputElement(element: HTMLElement): boolean {
    return (
      element.tagName === 'INPUT' ||
      element.tagName === 'TEXTAREA' ||
      element.isContentEditable
    );
  }

  /**
   * Handle input focus event
   */
  private handleInputFocus(element: HTMLElement): void {
    // Update initial height before keyboard appears
    this.updateInitialViewportHeight();

    // Multiple checks with increasing delays to catch keyboard appearance
    // Tablets, especially iPads, can have delayed keyboard appearance
    const delays = this.isTablet ? [150, 400, 700, 1000] : [100, 300, 500];

    delays.forEach((delay) => {
      const timeout = setTimeout(() => {
        this.checkKeyboardState();
      }, delay);
      this.focusCheckTimeouts.push(timeout);
    });
  }

  /**
   * Handle input blur event
   */
  private handleInputBlur(): void {
    // Check if there are other focused inputs
    const activeElement = document.activeElement;
    const hasOtherFocusedInput = activeElement && this.isInputElement(activeElement as HTMLElement);

    if (!hasOtherFocusedInput) {
      // Multiple checks with increasing delays to catch keyboard disappearance
      const delays = this.isTablet ? [150, 400, 700] : [100, 300];

      delays.forEach((delay) => {
        const timeout = setTimeout(() => {
          this.checkKeyboardState();
        }, delay);
        this.focusCheckTimeouts.push(timeout);
      });
    }
  }

  /**
   * Handle viewport change (debounced)
   */
  private handleViewportChange(): void {
    if (this.resizeDebounceTimeout) {
      clearTimeout(this.resizeDebounceTimeout);
    }

    // Debounce viewport changes to avoid excessive checks
    this.resizeDebounceTimeout = setTimeout(() => {
      this.checkKeyboardState();
    }, 50);
  }

  /**
   * Check keyboard state based on viewport height
   */
  private checkKeyboardState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Get current viewport height
      const visualViewportHeight = window.visualViewport?.height;
      const windowHeight = window.innerHeight;
      const currentHeight = visualViewportHeight || windowHeight;

      // If initial height is 0, update it now
      if (this.initialViewportHeight === 0) {
        this.initialViewportHeight = currentHeight;
        this.lastKnownHeight = currentHeight;
        return;
      }

      // Calculate height difference
      const heightDifference = this.initialViewportHeight - currentHeight;

      // Device-specific thresholds
      // Tablets (especially iPads) have smaller keyboards or different behavior
      let threshold: number;
      let hideThreshold: number;

      if (this.isIPad) {
        // iPad: Smaller keyboard, more sensitive detection
        threshold = 80;
        hideThreshold = 40;
      } else if (this.isTablet) {
        // Other tablets: Medium threshold
        threshold = 100;
        hideThreshold = 50;
      } else {
        // Phones: Standard threshold
        threshold = 150;
        hideThreshold = 50;
      }

      // Determine keyboard state
      const wasVisible = this.keyboardVisibleSubject.value;
      let isVisible = false;

      if (heightDifference > threshold) {
        // Significant height decrease = keyboard visible
        isVisible = true;
      } else if (Math.abs(heightDifference) < hideThreshold) {
        // Close to initial height = keyboard hidden
        isVisible = false;
        // Update initial height when keyboard is hidden (in case orientation changed)
        if (!wasVisible) {
          this.initialViewportHeight = currentHeight;
        }
      } else {
        // In between: keep current state (hysteresis to prevent flickering)
        isVisible = wasVisible;
      }

      // Update state if changed
      if (isVisible !== wasVisible) {
        this.keyboardVisibleSubject.next(isVisible);
        this.updateBodyClass(isVisible);
        
        this.logger.log('Keyboard state changed', {
          visible: isVisible,
          heightDifference,
          currentHeight,
          initialHeight: this.initialViewportHeight,
          deviceType: this.deviceType
        });
      }

      this.lastKnownHeight = currentHeight;
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'checkKeyboardState' });
    }
  }

  /**
   * Update body class for CSS styling
   */
  private updateBodyClass(isVisible: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (isVisible) {
        document.body.classList.add('keyboard-visible');
      } else {
        document.body.classList.remove('keyboard-visible');
      }
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'updateBodyClass' });
    }
  }

  /**
   * Cleanup all listeners
   */
  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Clear all timeouts
      this.focusCheckTimeouts.forEach(timeout => clearTimeout(timeout));
      this.focusCheckTimeouts = [];
      
      if (this.resizeDebounceTimeout) {
        clearTimeout(this.resizeDebounceTimeout);
      }

      // Remove viewport listeners
      if (this.visualViewportListener && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.visualViewportListener);
        window.visualViewport.removeEventListener('scroll', this.visualViewportListener);
      }

      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
      }

      // Remove orientation listeners
      if (this.orientationChangeListener) {
        window.removeEventListener('orientationchange', this.orientationChangeListener);
        window.removeEventListener('resize', this.orientationChangeListener);
      }

      // Remove input listeners
      if (this.inputFocusListener) {
        document.removeEventListener('focusin', this.inputFocusListener, true);
      }

      if (this.inputBlurListener) {
        document.removeEventListener('focusout', this.inputBlurListener, true);
      }

      // Remove body class
      document.body.classList.remove('keyboard-visible');
    } catch (error) {
      this.logger.error('KeyboardDetectionService', error, { operation: 'ngOnDestroy' });
    }
  }
}
